import { useEffect, useState } from "react";
import { Wallet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useFideicomiso } from "../lib/fideicomiso";
import { useAuth } from "../lib/auth";
import { ROLES } from "../lib/roles";
import { Button } from "../components/ui/button";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const CLASES = ["efectivo", "credito", "valor", "inmueble", "accion", "contrato", "prop_intelectual"];

export default function Patrimonio() {
    const { selected } = useFideicomiso();
    const { hasRole, user } = useAuth();
    const [activos, setActivos] = useState([]);
    const [movs, setMovs] = useState([]);
    const [movOpen, setMovOpen] = useState(false);
    const [movForm, setMovForm] = useState({ tipo: "aportacion", justificacion: "", activo_ids: [] });
    const [busy, setBusy] = useState(false);

    const canPropose = hasRole([ROLES.OPERACIONES, ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.ADMIN_TI]);
    const canApprove = hasRole([ROLES.TESORERIA, ROLES.DIRECCION, ROLES.ADMIN_TI, ROLES.ADMINISTRADOR_FIDUCIARIO]);
    const canExecute = hasRole([ROLES.TESORERIA, ROLES.OPERACIONES, ROLES.ADMIN_TI]);

    const load = async () => {
        if (!selected) return;
        const [{ data: a }, { data: m }] = await Promise.all([
            api.get(`/fideicomisos/${selected.id}/activos`),
            api.get(`/movimientos-patrimonio?fideicomiso_id=${selected.id}`),
        ]);
        setActivos(a); setMovs(m);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected?.id]);

    const totalPorClase = activos.reduce((acc, a) => {
        const k = a.clase;
        const v = parseFloat(a.valuacion_actual?.valor || 0);
        const mon = a.valuacion_actual?.moneda || "MXN";
        acc[k] = acc[k] || { count: 0, MXN: 0, USD: 0, EUR: 0 };
        acc[k].count += 1;
        acc[k][mon] = (acc[k][mon] || 0) + v;
        return acc;
    }, {});

    const submitMov = async (e) => {
        e.preventDefault();
        if (movForm.activo_ids.length === 0) return toast.error("Selecciona ≥1 activo");
        setBusy(true);
        try {
            const isEntry = movForm.tipo === "aportacion";
            await api.post(`/movimientos-patrimonio?fideicomiso_id=${selected.id}`, {
                tipo: movForm.tipo, fecha_efectiva: new Date().toISOString().slice(0, 10),
                activos_entrada: isEntry ? movForm.activo_ids : [],
                activos_salida: isEntry ? [] : movForm.activo_ids,
                justificacion: movForm.justificacion,
            });
            toast.success("Movimiento propuesto");
            setMovOpen(false); setMovForm({ tipo: "aportacion", justificacion: "", activo_ids: [] });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const action = async (id, path) => {
        try {
            await api.post(`/movimientos-patrimonio/${id}/${path}`);
            toast.success(`OK: ${path}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    if (!selected) return <div className="p-10 text-muted-foreground text-sm">Selecciona un fideicomiso.</div>;

    return (
        <div className="p-8 lg:p-10 max-w-[1600px] space-y-6" data-testid="patrimonio-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Núcleo · {selected.numero_interno}
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <Wallet className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Patrimonio
                    </h1>
                </div>
                {canPropose && (
                    <Dialog open={movOpen} onOpenChange={setMovOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-movimiento-btn">Nuevo movimiento</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Nuevo movimiento patrimonial</DialogTitle></DialogHeader>
                            <form onSubmit={submitMov} className="space-y-3">
                                <div><Label>Tipo</Label>
                                    <Select value={movForm.tipo} onValueChange={(v) => setMovForm({ ...movForm, tipo: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {["aportacion", "sustitucion", "liberacion", "reversion", "adjudicacion", "venta", "revaluacion"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Activos ({movForm.activo_ids.length})</Label>
                                    <div className="max-h-56 overflow-y-auto border border-border rounded-sm p-2 text-sm">
                                        {activos.map((a) => (
                                            <label key={a.id} className="flex items-center gap-2 py-1 hover:bg-muted rounded-sm px-1">
                                                <input type="checkbox"
                                                    checked={movForm.activo_ids.includes(a.id)}
                                                    onChange={() => setMovForm((f) => ({
                                                        ...f, activo_ids: f.activo_ids.includes(a.id)
                                                            ? f.activo_ids.filter((x) => x !== a.id)
                                                            : [...f.activo_ids, a.id]
                                                    }))} />
                                                <span>{a.identificador} · {a.clase} · {a.estado}</span>
                                                {a.gravamenes?.length > 0 && <span className="badge-mock text-[9px]">gravamen</span>}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div><Label>Justificación</Label>
                                    <Textarea required value={movForm.justificacion}
                                        onChange={(e) => setMovForm({ ...movForm, justificacion: e.target.value })}
                                        data-testid="mov-justificacion" />
                                    <div className="text-[10px] text-muted-foreground mt-1">
                                        Para liberar activos con gravamen, incluye "OVERRIDE_GRAVAMEN" en la justificación.
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button type="submit" disabled={busy} data-testid="submit-mov">
                                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Proponer"}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* KPIs por clase */}
            <div className="grid md:grid-cols-4 gap-3">
                {Object.entries(totalPorClase).map(([k, v]) => (
                    <div key={k} className="kpi-card" data-testid={`kpi-clase-${k}`}>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
                        <div className="kpi-value tabular text-xl">{v.count}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                            {v.MXN > 0 && <div>MXN {v.MXN.toLocaleString()}</div>}
                            {v.USD > 0 && <div>USD {v.USD.toLocaleString()}</div>}
                        </div>
                    </div>
                ))}
            </div>

            {/* Inventario */}
            <div>
                <h2 className="font-display text-2xl mb-3">Inventario ({activos.length})</h2>
                <div className="rounded-md border border-border overflow-hidden bg-card">
                    <table className="trf-table" data-testid="activos-table">
                        <thead><tr><th>Identificador</th><th>Clase</th><th>Estado</th><th>Valor</th><th>Moneda</th><th>Gravámenes</th></tr></thead>
                        <tbody>
                            {activos.map((a) => (
                                <tr key={a.id} data-testid={`activo-row-${a.id}`}>
                                    <td className="font-medium">{a.identificador}</td>
                                    <td><span className="badge-gold">{a.clase}</span></td>
                                    <td>
                                        <span className={a.estado === "activo" ? "badge-exito" : "badge-gold"}>{a.estado}</span>
                                    </td>
                                    <td className="tabular">{parseFloat(a.valuacion_actual?.valor || 0).toLocaleString()}</td>
                                    <td className="text-xs">{a.valuacion_actual?.moneda}</td>
                                    <td className="text-xs">{a.gravamenes?.length > 0 ? <span className="badge-mock">{a.gravamenes.length}</span> : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Movimientos con maker-checker */}
            <div>
                <h2 className="font-display text-2xl mb-3">Movimientos ({movs.length})</h2>
                <div className="rounded-md border border-border overflow-hidden bg-card">
                    <table className="trf-table" data-testid="movs-table">
                        <thead><tr>
                            <th>Fecha</th><th>Tipo</th><th>Propuesto por</th><th>Aprobado por</th>
                            <th>Ejecutado por</th><th>Estado</th><th>Acciones</th>
                        </tr></thead>
                        <tbody>
                            {movs.map((m) => (
                                <tr key={m.id}>
                                    <td className="tabular text-xs">{m.fecha_efectiva}</td>
                                    <td><span className="badge-gold">{m.tipo}</span></td>
                                    <td className="text-xs">{m.propuesto_por}</td>
                                    <td className="text-xs">{m.aprobado_por || "—"}</td>
                                    <td className="text-xs">{m.ejecutado_por || "—"}</td>
                                    <td>
                                        <span className={m.estado === "ejecutado" ? "badge-exito" : m.estado === "rechazado" ? "badge-mock" : "badge-gold"}>{m.estado}</span>
                                    </td>
                                    <td className="flex flex-wrap gap-1">
                                        {canApprove && m.estado === "propuesto" && m.propuesto_por !== user?.email && (
                                            <Button size="sm" variant="ghost" onClick={() => action(m.id, "aprobar")}
                                                data-testid={`aprobar-movimiento-${m.id}`}>Aprobar</Button>
                                        )}
                                        {canExecute && m.estado === "aprobado" && m.propuesto_por !== user?.email && m.aprobado_por !== user?.email && (
                                            <Button size="sm" variant="ghost" onClick={() => action(m.id, "ejecutar")}
                                                data-testid={`ejecutar-movimiento-${m.id}`}>Ejecutar</Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {movs.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Sin movimientos.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
